/**
 * Central registry of feature gate keys used in the application.
 * Add new feature gate keys here as string constants.
 */
export const featureGateKeys = {
  /**
   * PAU_UNREAD_INDICATOR
   * @link https://switcheroo.atlassian.com/ui/gates/7551c1a2-a8d2-4131-86d0-e72a5d827fff/key/pau-unread-indicator
   * @description Controls the unread indicator feature on Public App Updates.
   * When enabled: blue dot on unread release note rows, bold title/app for
   * unread rows, and 'New this week' filter chip in the filter bar.
   * Uses localStorage to track read state (requires analytics consent).
   * OFF by default — safe to merge at any time.
   * @returns true if the unread indicator should be shown
   */
  unreadIndicator: 'pau-unread-indicator',
  /**
   * PAU_AI_IMPACT_AND_CONTEXT_TAGS
   * @link https://hello.atlassian.net/browse/FD-202623
   * @description Controls visibility of AI-generated Impact rating and
   * Contextual tags columns on the list and details pages.
   * OFF by default — safe to merge at any time.
   * @returns true if the AI Impact and Contextual tags columns should be shown
   */
  aiImpactAndContextTags: 'public_app_updates_ai_impact_and_context_tags',
  /**
   * PAU_GA_RELEASE_CHANGES
   * @link https://switcheroo.atlassian.com/ui/gates/80464c07-8344-48ed-a94e-624d44d6448a/key/pau-ga-release-changes
   * @description Controls visibility of the "Bundled changes" tab (at
   * /release-notes/bundled-changes) on the App Updates list page.
   * When disabled, direct navigation to that path redirects to /release-notes.
   * OFF by default — safe to merge at any time.
   * @returns true if the "Bundled changes" tab should be shown
   */
  gaReleaseChanges: 'pau-ga-release-changes',
  /**
   * A fake gate key used only in unit tests to exercise the fg() utility.
   * Not a real Switcheroo gate — do not use in production code.
   */
  testOnly: 'test-gate-for-unit-tests',
} as const;

export type FeatureGateValue =
  (typeof featureGateKeys)[keyof typeof featureGateKeys];
